import {h, isRef, resolveComponent, unref, watch} from "vue";
import moment from 'moment';
import 'moment/locale/ar';
import {LocationQuery, RouteLocationRaw, useRoute} from "vue-router";
import {MaybeRefOrGetter, useDateFormat} from "@vueuse/core";
import ObjectChecker from "@/utils/ObjectChecker.ts";
import router from "@/router";
import {HOST_PROVIDERS, months, ROLE_LEVEL, Roles} from "@/utils/constants/common.constants.ts";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import {ModalProps} from "@nuxt/ui/components/Modal.vue";
import {AttendanceStatus, IAttendance, ILesson, IPayment, Subject} from "@/types/api.ts";
import {useAuthStore} from "@/store/auth-store.ts";
import {ButtonProps} from "@nuxt/ui/components/Button.vue";
import {BadgeProps} from "@nuxt/ui/components/Badge.vue";

export default class CommonUtils {
  static images = import.meta.glob('@/assets/images/**/*', {eager: true, import: 'default'});

  static getImgUrl(imgPath?: string): string | undefined {
    if (!imgPath)
      return;
    const base = "/src/assets/images";
    const image = this.images[base + imgPath];
    if (ObjectChecker.isString(image))
      return image;
  }

  static toSlug(title: string) {
    return title.replaceAll(" ", "-");
  }

  static slugToTitle(slug: string) {
    return slug.split("-").map(word => word[0].toUpperCase() + word.substring(1)).join(" ")
  }

  static onRouteChanged(cb: () => void) {
    const route = useRoute();
    watch(() => route.fullPath, () => {
      cb();
    },{immediate:true})
  }

  static onQueryChanged(cb: (query: LocationQuery) => void) {
    const route = useRoute();
    watch(() => route.query, () => cb(route.query))
  }

  static unrefs(obj: Record<string, MaybeRefOrGetter<any>>) {
    const objCopy = obj;
    for (const key in objCopy) {
      if (isRef(objCopy[key]))
        objCopy[key] = unref(objCopy[key])
    }
    return objCopy;
  }

  static navigateTo(to: RouteLocationRaw) {
    router.push(to).then()
  }

  static async waitFor(ms: number = 250) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  static getDisabledUI() {
    return {ui: {base: 'rounded-lg disabled:!disabled-btn aria-disabled:!disabled-btn'}}
  }

  static toCapitalize(text?: string): string {
    if (!text)
      return '';
    return text.split(" ").reduce((sentence, acc) => sentence + " " + acc.charAt(0).toUpperCase() + acc.substring(1), "")
  }

  static formattedDate(date: Date | string | undefined) {
    return useDateFormat(date, 'YYYY-MM-DD HH:mm').value
  }

  static toOTP(otp: string[]): string {
    return otp.reduce((a, c) => a + c, "");
  }

  static saveToStorage(key: string, data: any) {
    localStorage.setItem(key, JSON.stringify(data))
  }

  static getFromStorage<T>(key: any): T | null {
    if (ObjectChecker.isNotString(key))
      key = key.toString();
    const store = localStorage.getItem(key);
    if (ObjectChecker.isNull(store))
      return null;
    else
      return JSON.parse(store);
  }

  static toSingleWord(text?: string): string {
    if (ObjectChecker.isEmptyOrNull(text))
      return "";
    let sentence: string = "";
    for (const word of text.split(" ")) {
      sentence += word + "<br />";
    }
    return sentence;
  }

  static toArabicDate(date: Date | string | undefined): string {
    if (!date) return '';
    return moment(date)
      .locale('ar')
      .calendar(null, {
        sameDay: '[اليوم] HH:mm',
        lastDay: '[أمس] HH:mm',
        lastWeek: 'YYYY-MM-DD',
        sameElse: 'YYYY-MM-DD'
      });
  }

  static toSimpleDate(date?: Date | string): string {
    if (ObjectChecker.isEmptyOrNull(date))
      return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  static toArabicTime(date: Date): string {
    return new Date(date).toLocaleString("ar-EG", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  }

  static toArabicDay(date: string | Date | undefined): string {
    if (!date)
      return '';
    return new Date(date).toLocaleString('ar-EG', {
      weekday: 'long',
    });
  }

  static callBackIfNullOrUndefined(o: unknown, cb: NonNullable<any>) {
    if (ObjectChecker.isNullOrUndefined(o))
      return cb;
    return o;
  }

  static toQueryString<T>(query?: Partial<T>): string {
    return Object.entries(this.callBackIfNullOrUndefined(query, {}))
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join("&");
  }

  static getTableHeader(column: any, label: string) {
    const isSorted = column.getIsSorted()
    return h(resolveComponent("UButton"), {
      color: 'neutral',
      variant: 'ghost',
      label,
      icon: isSorted
        ? isSorted === 'asc'
          ? 'i-lucide-arrow-up-narrow-wide'
          : 'i-lucide-arrow-down-wide-narrow'
        : 'i-lucide-arrow-up-down',
      class: '-mx-2.5',
      onClick: () => column.toggleSorting(column.getIsSorted() === 'asc')
    })
  }

  static getMonthLabel(monthValue: number): string {
    return months.find(m => m.value == monthValue)?.['label'] ?? ''
  }

  static async confirmation(props: ModalProps) {
    const overlay = useOverlay();
    const confirmModal = overlay.create(ConfirmationModal)
    const result: boolean = await confirmModal.open(props);
    return result;
  }

  static getLessonTitle(lesson: ILesson | null) {
    if (!lesson)
      return '';
    let title = lesson.title;
    if (ObjectChecker.isEmptyOrNull(title))
      title = CommonUtils.toArabicDay(lesson.createdAt);
    title = `حصة: ${title}`
    return title;
  }

  static cleanShallow<T extends Record<string, any>>(obj: T): Partial<T> {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([_, v]) =>
          v !== null &&
          v !== undefined &&
          v !== "" &&
          !(Array.isArray(v) && v.length === 0) &&
          !(typeof v === "object" && Object.keys(v).length === 0)
      )
    ) as Partial<T>;
  }

  static toCallBackIfEmptyOrNull(o: unknown, cb: NonNullable<any>) {
    if (JSON.stringify(o) === JSON.stringify([
      {
        "student": {
          "groups": {}
        },
        "previousStatus": "لا يوجد",
        "payments": []
      }
    ]))
      return cb;
    if (ObjectChecker.isNotEmptyOrNullish(o))
      return o;
    else
      return cb;
  }

  static getNextDateToPay(payments?: IPayment[] | null, lastPayment?: Partial<Pick<IPayment, "month" | "year">>): {month?:IPayment['month'],year:IPayment['year']} {
    const currentMonth = null;
    const currentYear = new Date().getFullYear();
    let nextMonthToPaid: number|null;
    let nextYear: number;
    const paymentsOfStudent = payments;
    if (paymentsOfStudent?.length) {
      const lastPaid = paymentsOfStudent.reduce((max, curr) => {
        if (
          curr.year > max.year ||
          (curr.year === max.year && curr.month > max.month)
        ) {
          return curr;
        }
        return max;
      });
      if (lastPaid.month === 12) {
        nextMonthToPaid = 1;
        nextYear = lastPaid.year + 1;
      } else {
        nextMonthToPaid = lastPaid.month + 1;
        nextYear = lastPaid.year;
      }
    } else if (lastPayment && lastPayment.month && lastPayment.year) {
      if (lastPayment.month === 12) {
        nextMonthToPaid = 1;
        nextYear = lastPayment.year + 1;
      } else {
        nextMonthToPaid = lastPayment.month + 1;
        nextYear = lastPayment.year;
      }
    } else {
      nextMonthToPaid = currentMonth;
      nextYear = currentYear;
    }

    return {
      month: nextMonthToPaid,
      year: nextYear
    }
  }

  static toSubjectKey(subjectValue: Subject): Lowercase<keyof typeof Subject> | undefined {
    const key = (Object.keys(Subject) as Array<keyof typeof Subject>).find(key => Subject[key] == subjectValue);
    return key?.toLowerCase() as Lowercase<keyof typeof Subject> | undefined;
  }

  static canPerform(userRole: Roles, requiredRole?: Roles): boolean {
    if (!requiredRole)
      return false;
    let rolelevelElement = ROLE_LEVEL[requiredRole];
    if (!rolelevelElement)
      return false;
    return ROLE_LEVEL[userRole] >= rolelevelElement;
  };

  static toRoleLevel(role: Roles) {
    return ROLE_LEVEL[role];
  }

  static levelToRole(level: number): Roles | undefined {
    return (Object.keys(ROLE_LEVEL) as Roles[]).find(
      (role) => ROLE_LEVEL[role] === level
    );
  }

  static withOriginal<T>(data?: T[]) {
    if (ObjectChecker.isEmptyOrNull(data))
      return [];
    else
      return data.map(d => {
        this.addOriginal(d);
        return d;
      })
  }

  static hasHigherRoleLevel(givenUserRole?: Roles): boolean {
    if (!givenUserRole)
      givenUserRole = Roles.Student;
    const higherRole = CommonUtils.toRoleLevel(givenUserRole) + 1;
    let requiredRole = (CommonUtils.levelToRole(higherRole)) as Roles;
    return CommonUtils.canPerform(useAuthStore().getUserRole, requiredRole)
  }

  static hasSameRoleLevel(givenUserRole?: Roles): boolean {
    if (!givenUserRole)
      givenUserRole = Roles.Student;
    const roleLevel = CommonUtils.toRoleLevel(givenUserRole);
    let requiredRole = (CommonUtils.levelToRole(roleLevel)) as Roles;
    return CommonUtils.canPerform(useAuthStore().getUserRole, requiredRole)
  }

  static hasSameRoleLevelOrHigher(givenUserRole?: Roles): boolean {
    return this.hasHigherRoleLevel(givenUserRole) || this.hasSameRoleLevel(givenUserRole);
  }

  static getDisabledButtonUI(): Partial<ButtonProps> {
    return {
      ui: {
        base: 'ui-disabled-btn'
      },
      loadingIcon: "i-lucide-loader"
    }
  }

  static setValue(obj: any, path: string, value: any) {
    const keys = path.split('.'); // "group.physics" => ["group","physics"]
    let temp = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in temp) || typeof temp[key] !== 'object') {
        temp[key] = {};
      }
      temp = temp[key];
    }

    temp[keys[keys.length - 1]] = value;
  }

  static getValue<T>(obj: any, path: string): T | undefined {
    const keys = path.split('.');
    let temp = obj;

    for (const key of keys) {
      if (temp == null) return undefined; // stops if undefined/null
      temp = temp[key];
    }

    return temp;
  }

  static attendanceStatusToBadgeProps(status?: IAttendance['status']): BadgeProps {
    let badgeProps: BadgeProps = {};
    switch (status) {
      case AttendanceStatus.Present:
        badgeProps = {
          color: "success",
          variant: "subtle"
        }
        break;
      case  AttendanceStatus.Late:
        badgeProps = {
          color: "warning",
          variant: "outline"
        }
        break;
      case  AttendanceStatus.Absent:
        badgeProps = {
          color: "error",
          variant: "soft"
        }
        break;
      default:
        badgeProps = {
          color: "neutral",
          variant: "soft"
        }
    }
    badgeProps.label = status
    return badgeProps;
  }

  static formatPaymentToDate(payment: IPayment): string {
    const year = payment.year;
    const month = payment.month;
    return `${year}-${month}`
  }

  static toMoney(amount: number): string {
    return new Intl.NumberFormat("ar-EG", {
      style: "currency",
      currency: "EGP",
    }).format(amount);
  }

  static getLessonSubtitle(lesson: ILesson | null) {
    if (!lesson)
      return '';
    let text: string = "";
    if (ObjectChecker.isNotEmptyOrNull(lesson.classLevel))
      text += lesson.classLevel;
    if (ObjectChecker.isNotEmptyOrNull(lesson.subject))
      text += " - " + lesson.subject;
    if (ObjectChecker.isNotEmptyOrNull(lesson.group?.name))
      text += " - " + "مجموعة" + " " + lesson.group?.name;
    return text;
  }

  static setInStorage(key: any, data: any): void {
    if (ObjectChecker.isNotString(key))
      key = key.toString();
    localStorage.setItem(key, JSON.stringify(data));
  }

  static addOriginal(record: any): void {
    Object.assign(record, {_original: {...record}});
  }

  static isVercelProvider() {
    return import.meta.env.VITE_API_PROVIDER === HOST_PROVIDERS.VERCEL
  }

  static isProduction(){
    return import.meta.env.PROD;
  }

  static isVercelProduction(){
    return this.isVercelProvider() && this.isProduction();
  }

  static async copyToClipboard(text: string) {
    await navigator.clipboard.writeText(text);
  }
}